<script setup lang="js">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useBusinessProcessStore } from '@/stores/businessProcess.store';
import ModalTemplateSide from '@/components/modals/ModalTemplateSide.vue';
import useModal from '@/stores/modal.store';
import { useToastStore } from '@/stores/toast.store';
import { useRouteParams } from '@vueuse/router';

const bpStore = useBusinessProcessStore();
const { promoteBPRequest, bp } = storeToRefs(bpStore);
const { closeModal } = useModal();
const toastStore = useToastStore();
const projectId = useRouteParams('projectId');

// На бой уходит зафиксированное, а не то, что в работе. Пока жив черновик,
// last_version во вьюхе — уже его номер, выпущенная версия на единицу меньше.
const releasedVersion = computed(() =>
  bp.value.stable ? bp.value.last_version : bp.value.last_version - 1
);
const prodVersion = computed(() => bp.value.prod_version ?? null);
const neverFixated = computed(() => releasedVersion.value < 1);
const alreadyOnProd = computed(() => prodVersion.value === releasedVersion.value);
const canPromote = computed(() => !neverFixated.value && !alreadyOnProd.value);

function promote() {
  const name = bp.value.name;

  bpStore.promoteBP(projectId.value, bp.value.id).then(({ to }) => {
    closeModal();
    toastStore.success(`«${name}» выложен на бой, версия ${to}`);
  });
}
</script>

<template>
  <ModalTemplateSide
    @submit="promote"
    :disableButtons="promoteBPRequest.isLoading || !canPromote"
  >
    <template v-slot:title>Выложить на бой</template>
    <template v-slot:body>
      <p class="mt-3">{{ bp.name }}</p>

      <table class="table gray-table mt-3">
        <tr>
          <td>Сейчас на бою</td>
          <td>{{ prodVersion ? `v${prodVersion}` : 'не выкладывался' }}</td>
        </tr>
        <tr>
          <td>Уйдёт на бой</td>
          <td>{{ neverFixated ? '—' : `v${releasedVersion}` }}</td>
        </tr>
      </table>

      <p v-if="neverFixated" class="error-mesage">
        Бизнес-процесс ещё не фиксировался — выкладывать нечего.
      </p>
      <p v-else-if="alreadyOnProd" class="error-mesage">
        Эта версия уже на бою. Чтобы выложить правки, сначала зафиксируйте их.
      </p>
      <p v-else-if="!bp.stable" class="mt-3">
        У бизнес-процесса есть незафиксированные правки — на бой они не поедут,
        уйдёт последняя зафиксированная версия.
      </p>

      <p v-if="promoteBPRequest.isLoading" class="mt-3">Подождите. Идет запрос...</p>

      <p v-if="promoteBPRequest.error" class="error-mesage">
        {{ promoteBPRequest.error?.message ?? promoteBPRequest.error }}
      </p>
    </template>
    <template v-slot:submit>Выложить</template>
  </ModalTemplateSide>
</template>
